import React from 'react';

import {
    Box,
    Button,
    ButtonGroup,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';

export const getVoiceCampaignDefaultState = {
    telephony_backend: 'EXTERNAL',
    mode: 'BATCH',
    destination_type: 'URA',
    destination_mcdu: '',
    queue_id: '',
    dial_attempt: 1,
    retry_interval: 0,
    timeout: 30000,
    simultaneous_calls: 1,
    call_factor: 1,
    ads_billsec_limit: 0
}

export const ShouldSendVoice = (state) => {
    if (!state.destination_mcdu)
        return true
    if (!state.dial_attempt || Number(state.dial_attempt) < 1)
        return true
    if (state.retry_interval === '' || Number(state.retry_interval) < 0)
        return true
    if (!state.timeout || Number(state.timeout) < 1)
        return true
    if (!state.simultaneous_calls || Number(state.simultaneous_calls) < 1)
        return true
    if (state.call_factor === '' || Number(state.call_factor) < 0)
        return true
    if (state.ads_billsec_limit === '' || Number(state.ads_billsec_limit) < 0)
        return true
    return false
}

const VoiceCampaignConfig = ({
    parent,
    setParent,
    destinations,
    queues,
    advanced = false
}) => {
    const handleChange = (field) => (event) => {
        setParent({ [field]: event.target.value })
    }

    const handleNumberChange = (field) => (event) => {
        const value = event.target.value
        setParent({ [field]: value === '' ? '' : Number(value) })
    }

    const handleDestinationChange = (event) => {
        const destination = destinations.find((item) => String(item.mcdu || item.id) === String(event.target.value))
        setParent({
            destination_mcdu: event.target.value,
            destination_name: destination?.name || '',
            destination_ivr_id: destination?.ivr_id || null,
            destination_module_id: destination?.ivr_module_id || null
        })
    }

    const handleModeChange = (mode) => () => {
        setParent({ mode })
    }

    const handleBackendChange = (telephony_backend) => () => {
        setParent({
            telephony_backend,
            destination_mcdu: '',
            destination_name: '',
            destination_ivr_id: null,
            destination_module_id: null,
            queue_id: ''
        })
    }

    const hasQueue = parent.queue_id !== '' && parent.queue_id !== null && parent.queue_id !== undefined

    if (advanced) {
        return (
            <Box sx={{ gap: '1rem', display: 'flex', flexDirection: 'column' }}>
                <Typography className="typography-title" variant="h6" gutterBottom>
                    Configurações avançadas de discagem
                </Typography>
                <FormControl fullWidth size="small">
                    <InputLabel>Grupo/fila de voz</InputLabel>
                    <Select
                        label="Grupo/fila de voz"
                        value={parent.queue_id || ''}
                        onChange={handleChange('queue_id')}
                    >
                        <MenuItem value="">Sem grupo/fila</MenuItem>
                        {queues.map((queue) => (
                            <MenuItem key={queue.id} value={queue.id}>
                                {queue.name || queue.description || queue.queue}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Ligações simultâneas"
                    type="number"
                    variant="outlined"
                    value={parent.simultaneous_calls}
                    onChange={handleNumberChange('simultaneous_calls')}
                    disabled={hasQueue}
                    inputProps={{ min: 1 }}
                    fullWidth
                />
                <TextField
                    size="small"
                    label="Fator de multiplicação"
                    type="number"
                    variant="outlined"
                    value={parent.call_factor}
                    onChange={handleNumberChange('call_factor')}
                    inputProps={{ min: 0, step: 0.1 }}
                    fullWidth
                />
                <TextField
                    size="small"
                    label="Tempo mínimo atendida (segundos)"
                    type="number"
                    variant="outlined"
                    value={parent.ads_billsec_limit}
                    onChange={handleNumberChange('ads_billsec_limit')}
                    inputProps={{ min: 0 }}
                    fullWidth
                />
            </Box>
        )
    }

    return (
        <Box sx={{ gap: '1rem', display: 'flex', flexDirection: 'column' }}>
            <Typography className="typography-title" variant="h6" gutterBottom>
                Configuração de voz
            </Typography>
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    Telefonia
                </Typography>
                <ButtonGroup fullWidth size="small">
                    <Button
                        variant={(parent.telephony_backend || 'EXTERNAL') === 'EXTERNAL' ? 'contained' : 'outlined'}
                        onClick={handleBackendChange('EXTERNAL')}
                    >
                        Externa
                    </Button>
                    <Button
                        variant={parent.telephony_backend === 'LOCAL' ? 'contained' : 'outlined'}
                        onClick={handleBackendChange('LOCAL')}
                    >
                        Interna
                    </Button>
                </ButtonGroup>
            </Box>
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    Tipo de campanha
                </Typography>
                <ButtonGroup fullWidth size="small">
                    <Button
                        variant={(parent.mode || 'BATCH') === 'BATCH' ? 'contained' : 'outlined'}
                        onClick={handleModeChange('BATCH')}
                    >
                        Em lote
                    </Button>
                    <Button
                        variant={parent.mode === 'CONTINUOUS' ? 'contained' : 'outlined'}
                        onClick={handleModeChange('CONTINUOUS')}
                    >
                        Contínua
                    </Button>
                </ButtonGroup>
            </Box>
            <FormControl fullWidth size="small">
                <InputLabel>URA</InputLabel>
                <Select
                    label="URA"
                    value={parent.destination_mcdu || ''}
                    onChange={handleDestinationChange}
                >
                    {destinations.map((destination) => (
                        <MenuItem key={destination.id} value={destination.mcdu || destination.id}>
                            {destination.name} ({destination.mcdu || destination.id})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                size="small"
                label="Tentativas de discagem"
                type="number"
                variant="outlined"
                value={parent.dial_attempt}
                onChange={handleNumberChange('dial_attempt')}
                inputProps={{ min: 1 }}
                fullWidth
            />
            <TextField
                size="small"
                label="Intervalo entre tentativas (segundos)"
                type="number"
                variant="outlined"
                value={parent.retry_interval}
                onChange={handleNumberChange('retry_interval')}
                inputProps={{ min: 0 }}
                fullWidth
            />
            <TextField
                size="small"
                label="Timeout de discagem (ms)"
                type="number"
                variant="outlined"
                value={parent.timeout}
                onChange={handleNumberChange('timeout')}
                inputProps={{ min: 1 }}
                fullWidth
            />
        </Box>
    )
}

export default VoiceCampaignConfig
